<template>
  <div>
    <h3 class="subtitle-1 font-weight-bold mb-3">
      {{ $t('admin.productsDatabase.importWizard.steps.productDetails.title') }}
    </h3>
    
    <p class="mb-4">
      {{ $t('admin.productsDatabase.importWizard.steps.productDetails.description') }}
    </p>

    <v-container fluid>
      <v-row justify="center">
        <v-col cols="12">
          <v-card>
            <v-card-text>
              <v-form>
                <v-row>
                  <v-col cols="12">
                    <LocalBookTextInput
                      id="book-title"
                      :product-info-manager="productInfoManager"
                      field-name="title"
                      :label="$t('admin.productsDatabase.importWizard.steps.productDetails.titleField')"
                      :rules="[v => !!v || $t('errors.required', { field: $t('admin.productsDatabase.importWizard.steps.productDetails.titleField') })]"
                      required
                      @valid="titleFieldIsValid = $event"
                    />
                  </v-col>

                  <v-col cols="12">
                    <LocalBookTextArea
                      id="book-description"
                      :product-info-manager="productInfoManager"
                      field-name="description"
                      :label="$t('admin.productsDatabase.importWizard.steps.productDetails.descriptionField')"
                    />
                  </v-col>

                  <v-col
                    cols="12"
                    md="6"
                  >
                    <LocalBookTextInput
                      id="book-isbn"
                      :product-info-manager="productInfoManager"
                      field-name="isbn"
                      :label="$t('bookInfo.isbn')"
                    />
                  </v-col>

                  <v-col cols="12">
                    <AuthorSelectionPanel
                      v-model="selectedAuthors"
                      :book-id="bookId"
                    />
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import LocalBookTextInput from '@/components/LocalBookTextInput.vue';
import LocalBookTextArea from '@/components/LocalBookTextArea.vue';
import AuthorSelectionPanel from '@/components/AuthorSelectionPanel.vue';
import { removeIriId } from '@/iris';
import AuthorRolesMixin from '@/mixins/AuthorRolesMixin';

export default {
  name: 'ProductDetails',
  components: {
    LocalBookTextInput,
    LocalBookTextArea,
    AuthorSelectionPanel
  },
  mixins: [AuthorRolesMixin],
  props: {
    productInfoManager: {
      type: Object,
      required: true
    }
  },
  emits: ['valid'],
  data() {
    return {
      selectedAuthors: [],
      titleFieldIsValid: false
    };
  },
  computed: {
    bookId() {
      return this.productInfoManager?.getBookId() || '';
    },
    productInfo() {
      return this.productInfoManager?.getData();
    },
    isValid() {
      
      const hasAuthors = this.selectedAuthors && this.selectedAuthors.length > 0;
      
      return this.titleFieldIsValid && hasAuthors;
    }
  },
  watch: {
    productInfo: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.book) {
          // Set authors if available in bookAuthors
          if (newVal.book.bookAuthors && newVal.book.bookAuthors.length > 0) {
            this.selectedAuthors = newVal.book.bookAuthors.map(bookAuthor => ({
              id: bookAuthor.id,
              author: bookAuthor.author,
              role: bookAuthor.role?.description || 'Author',
              sequence: bookAuthor.sequence
            }));
          }
        }
      }
    },
    selectedAuthors: {
      handler(newVal) {
        try {
          if (!Array.isArray(newVal)) return;
          
          const bookAuthors = newVal.map(item => {
            if (!item || !item.author) {
              return {
                author: { id: '', _id: '', name: 'Unknown' },
                role: { id: 'author_roles/author', _id: 'author', description: 'Author' }
              };
            }
            
            const authorId = item.author.id || '';
            const authorName = item.author.name || 'Unknown';
            const author_id = item.author._id || this.extractIdFromUri(authorId);
            
            const roleId = this.getRoleIdByDescription(item.role || 'Author');
            
            return {
              id: item.id,
              author: {
                id: authorId,
                _id: author_id,
                name: authorName
              },
              role: {
                id: `author_roles/${roleId}`,
                _id: roleId,
                description: item.role || 'Author'
              }
            };
          });
          
          const currentBookAuthors = this.productInfo && this.productInfo.book ? this.productInfo.book.bookAuthors : [];
          if (JSON.stringify(bookAuthors) !== JSON.stringify(currentBookAuthors)) {
            if (this.productInfoManager) {
              // AuthorSelectionPanel already saved via createBookAuthor, just update local state
              this.productInfoManager.updateFieldAlreadySaved('book.bookAuthors', bookAuthors);
            }
          }
        } catch (error) {
          console.error('Error in selectedAuthors watcher:', error);
        }
      },
      deep: true
    },
    isValid(newVal) {
      this.$emit('valid', newVal);
    }
  },
  mounted() {
    // Emit initial validation state
    this.$emit('valid', this.isValid);
  },
  methods: {
    extractIdFromUri(uri) {
      return removeIriId(uri);
    }
  }
};
</script>

<style scoped>
.v-card {
  background-color: var(--v-backgroundSecondary-base);
}
</style>
